import * as i2 from '@angular/cdk/a11y';
import * as i3 from '@angular/cdk/layout';
import * as i1 from '@angular/cdk/overlay';
import { OverlayModule, OverlayConfig } from '@angular/cdk/overlay';
import { BasePortalOutlet, CdkPortalOutlet, PortalModule, ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { InjectionToken, Directive, ViewChild, Component, ChangeDetectionStrategy, ViewEncapsulation, Inject, NgModule, Injector, TemplateRef, Injectable, Optional, SkipSelf } from '@angular/core';
import { SbbCommonModule, Breakpoints } from '@sbb-esta/angular/core';
import { take, takeUntil } from 'rxjs/operators';
import { SbbIcon, SbbIconModule } from '@sbb-esta/angular/icon';
import { Subject } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';

/** Injection token that can be used to access the data that was passed into a notification toast. */
const SBB_NOTIFICATION_TOAST_DATA = new InjectionToken('SbbNotificationToastData');
class SbbNotificationToastConfig {
    constructor() {
        /** The politeness level for the AriaLiveAnnouncer announcement. */
        this.politeness = 'assertive';
        /** The length of time in milliseconds to wait before automatically dismissing the notification. */
        this.duration = 0;
        /** Data being injected into the child component. */
        this.data = null;
        /** Possible values for the notification's vertical position. */
        this.verticalPosition = 'bottom';
        /** The notification type */
        this.type = 'success';
    }
}

const SBB_NOTIFICATION_TOAST_ANIMATIONS = {
    /** Animation that shows and hides a notification toast. */
    notificationState: trigger('state', [
        state('void, hidden', style({
            transform: 'scale(0.8)',
            opacity: 0,
        })),
        state('visible', style({
            transform: 'scale(1)',
            opacity: 1,
        })),
        transition('* => visible', animate('150ms cubic-bezier(0, 0, 0.2, 1)')),
        transition('* => void, * => hidden', animate('75ms cubic-bezier(0.4, 0.0, 1, 1)', style({
            opacity: 0,
        }))),
    ]),
};

/**
 * Base class for notification toast container.
 * @docs-private
 */
class SbbNotificationToastContainerBase extends BasePortalOutlet {
    get _svgIcon() {
        switch (this.config.type) {
            case 'success':
                return 'tick-small';
            case 'error':
            case 'warn':
                return 'sign-exclamation-point-small';
            default:
                return 'circle-information-small';
        }
    }
    constructor(_ngZone, _elementRef, _changeDetectorRef, config) {
        super();
        this._ngZone = _ngZone;
        this._elementRef = _elementRef;
        this._changeDetectorRef = _changeDetectorRef;
        this.config = config;
        this._destroyed = false;
        /** Subject for notifying that the notification toast has exited from view. */
        this._onExit = new Subject();
        /** Subject for notifying that the notification toast has finished entering the view. */
        this._onEnter = new Subject();
        /** The state of the notification toast animations. */
        this._animationState = 'void';
        /**
         * Attaches a DOM portal to the notification toast container.
         * @deprecated To be turned into a method.
         * @breaking-change 10.0.0
         */
        this.attachDomPortal = (portal) => {
            this._assertNotAttached();
            const result = this._portalOutlet.attachDomPortal(portal);
            this._afterPortalAttached();
            return result;
        };
        // Based on the ARIA spec, `alert` and `status` roles have an
        // implicit `assertive` and `polite` politeness respectively.
        if (config.politeness === 'assertive' && !config.announcementMessage) {
            this._role = 'alert';
        }
        else if (config.politeness === 'off') {
            this._role = null;
        }
        else {
            this._role = 'status';
        }
    }
    /** Attach a component portal as content to this notification toast container. */
    attachComponentPortal(portal) {
        this._assertNotAttached();
        const result = this._portalOutlet.attachComponentPortal(portal);
        this._afterPortalAttached();
        return result;
    }
    /** Attach a template portal as content to this notification toast container. */
    attachTemplatePortal(portal) {
        this._assertNotAttached();
        const result = this._portalOutlet.attachTemplatePortal(portal);
        this._afterPortalAttached();
        return result;
    }
    /** Handle end of animations, updating the state of the notification toast. */
    onAnimationEnd(event) {
        const { fromState, toState } = event;
        if ((toState === 'void' && fromState !== 'void') || toState === 'hidden') {
            this._completeExit();
        }
        if (toState === 'visible') {
            // Note: we shouldn't use `this` inside the zone callback,
            // because it can cause a memory leak.
            const onEnter = this._onEnter;
            this._ngZone.run(() => {
                onEnter.next();
                onEnter.complete();
            });
        }
    }
    /** Begin animation of notification toast entrance into view. */
    enter() {
        if (!this._destroyed) {
            this._animationState = 'visible';
            this._changeDetectorRef.detectChanges();
        }
    }
    /** Begin animation of the notification toast exiting from view. */
    exit() {
        // Note: this one transitions to `hidden`, rather than `void`, in order to handle the case
        // where multiple notification toasts are opened in quick succession (e.g. two consecutive calls to
        // `SbbNotificationToast.open`).
        this._animationState = 'hidden';
        // Mark this element with an 'exit' attribute to indicate that the notification toast has
        // been dismissed and will soon be removed from the DOM. This is used by the notification toast
        // test harness.
        this._elementRef.nativeElement.setAttribute('sbb-exit', '');
        return this._onExit;
    }
    /** Makes sure the exit callbacks have been invoked when the element is destroyed. */
    ngOnDestroy() {
        this._destroyed = true;
        this._completeExit();
    }
    /**
     * Waits for the zone to settle before removing the element. Helps prevent
     * errors where we end up removing an element which is in the middle of an animation.
     */
    _completeExit() {
        this._ngZone.onMicrotaskEmpty.pipe(take(1)).subscribe(() => {
            this._onExit.next();
            this._onExit.complete();
        });
    }
    /**
     * Called after the portal contents have been attached. Can be
     * used to modify the DOM once it's guaranteed to be in place.
     */
    _afterPortalAttached() {
        const element = this._elementRef.nativeElement;
        const panelClasses = this.config.panelClass;
        if (panelClasses) {
            if (Array.isArray(panelClasses)) {
                // Note that we can't use a spread here, because IE doesn't support multiple arguments.
                panelClasses.forEach((cssClass) => element.classList.add(cssClass));
            }
            else {
                element.classList.add(panelClasses);
            }
        }
        // currently, only a centered position is supported
        element.classList.add('sbb-notification-toast-center');
    }
    /** Asserts that no content is already attached to the container. */
    _assertNotAttached() {
        if (this._portalOutlet.hasAttached()) {
            throw Error('Attempting to attach notification content after content is already attached');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToastContainerBase, deps: [{ token: i0.NgZone }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: SbbNotificationToastConfig }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.7", type: SbbNotificationToastContainerBase, viewQueries: [{ propertyName: "_portalOutlet", first: true, predicate: CdkPortalOutlet, descendants: true, static: true }], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToastContainerBase, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: i0.NgZone }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: SbbNotificationToastConfig }], propDecorators: { _portalOutlet: [{
                type: ViewChild,
                args: [CdkPortalOutlet, { static: true }]
            }] } });
class SbbNotificationToastContainer extends SbbNotificationToastContainerBase {
    _afterPortalAttached() {
        super._afterPortalAttached();
        if (this.config.verticalPosition === 'top') {
            this._elementRef.nativeElement.classList.add('sbb-notification-toast-top');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToastContainer, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.7", type: SbbNotificationToastContainer, isStandalone: true, selector: "sbb-notification-toast-container", host: { listeners: { "@state.done": "onAnimationEnd($event)" }, properties: { "class.sbb-notification-toast-success": "config.type === \"success\"", "class.sbb-notification-toast-info": "config.type === \"info\"", "class.sbb-notification-toast-warn": "config.type === \"warn\"", "class.sbb-notification-toast-error": "config.type === \"error\"", "attr.role": "_role", "@state": "_animationState" }, classAttribute: "sbb-notification-toast" }, usesInheritance: true, ngImport: i0, template: "<span class=\"sbb-notification-toast-icon-wrapper sbb-icon-fit\">\n  <sbb-icon [svgIcon]=\"_svgIcon\"></sbb-icon>\n</span>\n<div class=\"sbb-notification-toast-content\">\n  <ng-template cdkPortalOutlet></ng-template>\n</div>\n\n<button\n  (click)=\"exit()\"\n  class=\"sbb-notification-toast-icon-wrapper sbb-notification-toast-icon-close-wrapper sbb-icon-fit\"\n  type=\"button\"\n>\n  <sbb-icon svgIcon=\"cross-small\"></sbb-icon>\n</button>\n", styles: [".sbb-notification-toast{display:flex;min-height:calc(3.2rem*var(--sbb-scaling-factor));border-radius:var(--sbb-border-radius);color:var(--sbb-color-white);background-color:var(--sbb-color-background);font-size:var(--sbb-font-size);line-height:var(--sbb-line-height);padding-top:calc(0.8rem*var(--sbb-scaling-factor) - var(--sbb-border-width-thin));padding-bottom:calc(0.8rem*var(--sbb-scaling-factor) - var(--sbb-border-width-thin) - var(--sbb-line-height) + var(--sbb-icon-size-default));margin:calc(1.3333333333rem*var(--sbb-scaling-factor))}:where(html.sbb-lean) .sbb-notification-toast{padding-bottom:calc(0.8rem*var(--sbb-scaling-factor) - var(--sbb-border-width-thin))}.sbb-notification-toast-mobile .sbb-notification-toast{margin:calc(0.5333333333rem*var(--sbb-scaling-factor));max-width:100%;min-width:0;width:100%}.sbb-notification-toast-mobile{width:100%}.sbb-notification-toast-error{border:var(--sbb-border-width-thin) solid var(--sbb-notification-border-color-error);background-color:var(--sbb-notification-background-color-error)}:is(.sbb-notification-toast-error,.sbb-notification-toast-error :is(a,button)){color:var(--sbb-notification-label-color-error)}.sbb-notification-toast-success{border:var(--sbb-border-width-thin) solid var(--sbb-notification-border-color-success);background-color:var(--sbb-notification-background-color-success)}:is(.sbb-notification-toast-success,.sbb-notification-toast-success :is(a,button)){color:var(--sbb-notification-label-color-success)}.sbb-notification-toast-info{border:var(--sbb-border-width-thin) solid var(--sbb-notification-border-color-information);background-color:var(--sbb-notification-background-color-information)}:is(.sbb-notification-toast-info,.sbb-notification-toast-info :is(a,button)){color:var(--sbb-notification-label-color-information)}.sbb-notification-toast-warn{border:var(--sbb-border-width-thin) solid var(--sbb-notification-border-color-warn);background-color:var(--sbb-notification-background-color-warn)}:is(.sbb-notification-toast-warn,.sbb-notification-toast-warn :is(a,button)){color:var(--sbb-notification-label-color-warn)}.sbb-notification-toast-content{margin-left:calc(0.5333333333rem*var(--sbb-scaling-factor));margin-right:calc(1.6rem*var(--sbb-scaling-factor))}.sbb-notification-toast-icon-wrapper{min-width:var(--sbb-icon-size-default);width:var(--sbb-icon-size-default);height:var(--sbb-icon-size-default);margin-left:calc(1.0666666667rem*var(--sbb-scaling-factor));border:none;background:none;padding:0;align-self:flex-start}.sbb-notification-toast-icon-close-wrapper{cursor:pointer;margin-left:auto;margin-right:calc(1.4666666667rem*var(--sbb-scaling-factor))}"], dependencies: [{ kind: "component", type: SbbIcon, selector: "sbb-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon", "id"], exportAs: ["sbbIcon"] }, { kind: "directive", type: CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }], animations: [SBB_NOTIFICATION_TOAST_ANIMATIONS.notificationState], changeDetection: i0.ChangeDetectionStrategy.Default, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToastContainer, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-notification-toast-container', changeDetection: ChangeDetectionStrategy.Default, encapsulation: ViewEncapsulation.None, animations: [SBB_NOTIFICATION_TOAST_ANIMATIONS.notificationState], host: {
                        class: 'sbb-notification-toast',
                        '[class.sbb-notification-toast-success]': 'config.type === "success"',
                        '[class.sbb-notification-toast-info]': 'config.type === "info"',
                        '[class.sbb-notification-toast-warn]': 'config.type === "warn"',
                        '[class.sbb-notification-toast-error]': 'config.type === "error"',
                        '[attr.role]': '_role',
                        '[@state]': '_animationState',
                        '(@state.done)': 'onAnimationEnd($event)',
                    }, standalone: true, imports: [SbbIcon, CdkPortalOutlet], template: "<span class=\"sbb-notification-toast-icon-wrapper sbb-icon-fit\">\n  <sbb-icon [svgIcon]=\"_svgIcon\"></sbb-icon>\n</span>\n<div class=\"sbb-notification-toast-content\">\n  <ng-template cdkPortalOutlet></ng-template>\n</div>\n\n<button\n  (click)=\"exit()\"\n  class=\"sbb-notification-toast-icon-wrapper sbb-notification-toast-icon-close-wrapper sbb-icon-fit\"\n  type=\"button\"\n>\n  <sbb-icon svgIcon=\"cross-small\"></sbb-icon>\n</button>\n", styles: [".sbb-notification-toast{display:flex;min-height:calc(3.2rem*var(--sbb-scaling-factor));border-radius:var(--sbb-border-radius);color:var(--sbb-color-white);background-color:var(--sbb-color-background);font-size:var(--sbb-font-size);line-height:var(--sbb-line-height);padding-top:calc(0.8rem*var(--sbb-scaling-factor) - var(--sbb-border-width-thin));padding-bottom:calc(0.8rem*var(--sbb-scaling-factor) - var(--sbb-border-width-thin) - var(--sbb-line-height) + var(--sbb-icon-size-default));margin:calc(1.3333333333rem*var(--sbb-scaling-factor))}:where(html.sbb-lean) .sbb-notification-toast{padding-bottom:calc(0.8rem*var(--sbb-scaling-factor) - var(--sbb-border-width-thin))}.sbb-notification-toast-mobile .sbb-notification-toast{margin:calc(0.5333333333rem*var(--sbb-scaling-factor));max-width:100%;min-width:0;width:100%}.sbb-notification-toast-mobile{width:100%}.sbb-notification-toast-error{border:var(--sbb-border-width-thin) solid var(--sbb-notification-border-color-error);background-color:var(--sbb-notification-background-color-error)}:is(.sbb-notification-toast-error,.sbb-notification-toast-error :is(a,button)){color:var(--sbb-notification-label-color-error)}.sbb-notification-toast-success{border:var(--sbb-border-width-thin) solid var(--sbb-notification-border-color-success);background-color:var(--sbb-notification-background-color-success)}:is(.sbb-notification-toast-success,.sbb-notification-toast-success :is(a,button)){color:var(--sbb-notification-label-color-success)}.sbb-notification-toast-info{border:var(--sbb-border-width-thin) solid var(--sbb-notification-border-color-information);background-color:var(--sbb-notification-background-color-information)}:is(.sbb-notification-toast-info,.sbb-notification-toast-info :is(a,button)){color:var(--sbb-notification-label-color-information)}.sbb-notification-toast-warn{border:var(--sbb-border-width-thin) solid var(--sbb-notification-border-color-warn);background-color:var(--sbb-notification-background-color-warn)}:is(.sbb-notification-toast-warn,.sbb-notification-toast-warn :is(a,button)){color:var(--sbb-notification-label-color-warn)}.sbb-notification-toast-content{margin-left:calc(0.5333333333rem*var(--sbb-scaling-factor));margin-right:calc(1.6rem*var(--sbb-scaling-factor))}.sbb-notification-toast-icon-wrapper{min-width:var(--sbb-icon-size-default);width:var(--sbb-icon-size-default);height:var(--sbb-icon-size-default);margin-left:calc(1.0666666667rem*var(--sbb-scaling-factor));border:none;background:none;padding:0;align-self:flex-start}.sbb-notification-toast-icon-close-wrapper{cursor:pointer;margin-left:auto;margin-right:calc(1.4666666667rem*var(--sbb-scaling-factor))}"] }]
        }] });

/** Maximum amount of milliseconds that can be passed into setTimeout. */
const MAX_TIMEOUT = Math.pow(2, 31) - 1;
/** Reference to a notification toast dispatched from the notification toast service. */
class SbbNotificationToastRef {
    constructor(containerInstance, _overlayRef) {
        this._overlayRef = _overlayRef;
        /** Subject for notifying the user that the notification toast has been dismissed. */
        this._afterDismissed = new Subject();
        /** Subject for notifying the user that the notification toast has opened and appeared. */
        this._afterOpened = new Subject();
        this.containerInstance = containerInstance;
        containerInstance._onExit.subscribe(() => this._finishDismiss());
    }
    /** Dismisses the notification toast. */
    dismiss() {
        if (!this._afterDismissed.closed) {
            this.containerInstance.exit();
        }
        clearTimeout(this._durationTimeoutId);
    }
    /** Dismisses the notification toast after some duration */
    _dismissAfter(duration) {
        // Note that we need to cap the duration to the maximum value for setTimeout, because
        // it'll revert to 1 if somebody passes in something greater (e.g. `Infinity`). See #17234.
        this._durationTimeoutId = setTimeout(() => this.dismiss(), Math.min(duration, MAX_TIMEOUT));
    }
    /** Marks the notification toast as opened */
    _open() {
        if (!this._afterOpened.closed) {
            this._afterOpened.next();
            this._afterOpened.complete();
        }
    }
    /** Cleans up the DOM after closing. */
    _finishDismiss() {
        this._overlayRef.dispose();
        this._afterDismissed.next(null);
        this._afterDismissed.complete();
    }
    /** Gets an observable that is notified when the notification toast is finished closing. */
    afterDismissed() {
        return this._afterDismissed.asObservable();
    }
    /** Gets an observable that is notified when the notification toast has opened and appeared. */
    afterOpened() {
        return this.containerInstance._onEnter;
    }
}

/**
 * A component used to open as the default notification toast, matching digital.sbb.ch spec.
 * This should only be used internally by the notification toast service.
 */
class SbbSimpleNotification {
    constructor(notificationToastRef, data) {
        this.notificationToastRef = notificationToastRef;
        this.data = data;
    }
    /** Dismisses the notification toast. */
    dismiss() {
        this.notificationToastRef.dismiss();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSimpleNotification, deps: [{ token: SbbNotificationToastRef }, { token: SBB_NOTIFICATION_TOAST_DATA }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.7", type: SbbSimpleNotification, isStandalone: true, selector: "sbb-simple-notification", ngImport: i0, template: "<span>{{ data.message }}</span>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbSimpleNotification, decorators: [{
            type: Component,
            args: [{ selector: 'sbb-simple-notification', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, template: "<span>{{ data.message }}</span>\n" }]
        }], ctorParameters: () => [{ type: SbbNotificationToastRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SBB_NOTIFICATION_TOAST_DATA]
                }] }] });

class SbbNotificationToastModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToastModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToastModule, imports: [PortalModule,
            OverlayModule,
            SbbCommonModule,
            SbbIconModule,
            SbbNotificationToastContainer,
            SbbSimpleNotification], exports: [SbbNotificationToastContainer, SbbSimpleNotification] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToastModule, imports: [PortalModule,
            OverlayModule,
            SbbCommonModule,
            SbbIconModule,
            SbbNotificationToastContainer] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToastModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        PortalModule,
                        OverlayModule,
                        SbbCommonModule,
                        SbbIconModule,
                        SbbNotificationToastContainer,
                        SbbSimpleNotification,
                    ],
                    exports: [SbbNotificationToastContainer, SbbSimpleNotification],
                }]
        }] });

/** Injection token that can be used to specify default notification toast. */
const SBB_NOTIFICATION_TOAST_DEFAULT_OPTIONS = new InjectionToken('notification-default-options', {
    providedIn: 'root',
    factory: SBB_NOTIFICATION_TOAST_DEFAULT_OPTIONS_FACTORY,
});
/** @docs-private */
function SBB_NOTIFICATION_TOAST_DEFAULT_OPTIONS_FACTORY() {
    return new SbbNotificationToastConfig();
}
/** Service to dispatch notification toast messages. */
class SbbNotificationToast {
    /** Reference to the currently opened notification toast at *any* level. */
    get _openedNotificationRef() {
        const parent = this._parentNotification;
        return parent ? parent._openedNotificationRef : this._notificationRefAtThisLevel;
    }
    set _openedNotificationRef(value) {
        if (this._parentNotification) {
            this._parentNotification._openedNotificationRef = value;
        }
        else {
            this._notificationRefAtThisLevel = value;
        }
    }
    constructor(_overlay, _injector, _live, _breakpointObserver, _parentNotification, _defaultConfig) {
        this._overlay = _overlay;
        this._injector = _injector;
        this._live = _live;
        this._breakpointObserver = _breakpointObserver;
        this._parentNotification = _parentNotification;
        this._defaultConfig = _defaultConfig;
        /**
         * Reference to the current notification toast in the view *at this level* (in the Angular injector tree).
         * If there is a parent notification toast service, all operations should delegate to that parent
         * via `_openednotification toastRef`.
         */
        this._notificationRefAtThisLevel = null;
        /** The component that should be rendered as the notification toast's simple component. */
        this._simpleNotificationToastComponent = SbbSimpleNotification;
        /** The container component that attaches the provided template or component. */
        this._notificationToastContainerComponent = SbbNotificationToastContainer;
        /** The CSS class to applie for mobile mode. */
        this._mobileDeviceCssClass = 'sbb-notification-toast-mobile';
    }
    /**
     * Creates and dispatches a notification toast with a custom component for the content, removing any
     * currently opened notification toasts.
     *
     * @param component Component to be instantiated.
     * @param config Extra configuration for the notification toast.
     */
    openFromComponent(component, config) {
        return this._attach(component, config);
    }
    /**
     * Creates and dispatches a notification toast with a custom template for the content, removing any
     * currently opened notification toasts.
     *
     * @param template Template to be instantiated.
     * @param config Extra configuration for the notification toast.
     */
    openFromTemplate(template, config) {
        return this._attach(template, config);
    }
    /**
     * Opens a notification toast with a message and an optional action.
     * @param message The message to show in the notification toast.
     * @param action The label for the notification toast action.
     * @param config Additional configuration options for the notification toast.
     */
    open(message, config) {
        const mergedConfig = { ...this._defaultConfig, ...config };
        // Since the user doesn't have access to the component, we can
        // override the data to pass in our own message and action.
        mergedConfig.data = { message };
        // Since the notification toast has `role="alert"`, we don't
        // want to announce the same message twice.
        if (mergedConfig.announcementMessage === message) {
            mergedConfig.announcementMessage = undefined;
        }
        return this.openFromComponent(this._simpleNotificationToastComponent, mergedConfig);
    }
    /** Dismisses the currently-visible notification toast. */
    dismiss() {
        if (this._openedNotificationRef) {
            this._openedNotificationRef.dismiss();
        }
    }
    ngOnDestroy() {
        // Only dismiss the notification at the current level on destroy.
        if (this._notificationRefAtThisLevel) {
            this._notificationRefAtThisLevel.dismiss();
        }
    }
    /** Attaches the notification toast container component to the overlay. */
    _attachNotificationToastContainer(overlayRef, config) {
        const userInjector = config?.viewContainerRef?.injector;
        const injector = Injector.create({
            parent: userInjector || this._injector,
            providers: [
                {
                    provide: SbbNotificationToastConfig,
                    useValue: config,
                },
            ],
        });
        const containerPortal = new ComponentPortal(this._notificationToastContainerComponent, config.viewContainerRef, injector);
        const containerRef = overlayRef.attach(containerPortal);
        containerRef.instance.config = config;
        return containerRef.instance;
    }
    /** Places a new component or a template as the content of the notification toast container. */
    _attach(content, userConfig) {
        const config = { ...new SbbNotificationToastConfig(), ...this._defaultConfig, ...userConfig };
        const overlayRef = this._createOverlay(config);
        const container = this._attachNotificationToastContainer(overlayRef, config);
        const notificationRef = new SbbNotificationToastRef(container, overlayRef);
        if (content instanceof TemplateRef) {
            const portal = new TemplatePortal(content, null, {
                $implicit: config.data,
                notificationRef,
            });
            notificationRef.instance = container.attachTemplatePortal(portal);
        }
        else {
            const injector = this._createInjector(config, notificationRef);
            const portal = new ComponentPortal(content, undefined, injector);
            const contentRef = container.attachComponentPortal(portal);
            // We can't pass this via the injector, because the injector is created earlier.
            notificationRef.instance = contentRef.instance;
        }
        // Subscribe to the breakpoint observer and attach the notification-mobile class as
        // appropriate. This class is applied to the overlay element because the overlay must expand to
        // fill the width of the screen for full width notifications.
        this._breakpointObserver
            .observe(Breakpoints.MobileDevicePortrait)
            .pipe(takeUntil(overlayRef.detachments()))
            .subscribe((state) => {
            const classList = overlayRef.overlayElement.classList;
            state.matches
                ? classList.add(this._mobileDeviceCssClass)
                : classList.remove(this._mobileDeviceCssClass);
        });
        this._animateNotification(notificationRef, config);
        this._openedNotificationRef = notificationRef;
        return this._openedNotificationRef;
    }
    /** Animates the old notification toast out and the new one in. */
    _animateNotification(notificationRef, config) {
        // When the notification toast is dismissed, clear the reference to it.
        notificationRef.afterDismissed().subscribe(() => {
            // Clear the notification toast ref if it hasn't already been replaced by a newer notification toast.
            if (this._openedNotificationRef === notificationRef) {
                this._openedNotificationRef = null;
            }
            if (config.announcementMessage) {
                this._live.clear();
            }
        });
        if (this._openedNotificationRef) {
            // If a notification toast is already in view, dismiss it and enter the
            // new notification toast after exit animation is complete.
            this._openedNotificationRef.afterDismissed().subscribe(() => {
                notificationRef.containerInstance.enter();
            });
            this._openedNotificationRef.dismiss();
        }
        else {
            // If no notification toast is in view, enter the new notification toast.
            notificationRef.containerInstance.enter();
        }
        // If a dismiss timeout is provided, set up dismiss based on after the notification toast is opened.
        if (config.duration && config.duration > 0) {
            notificationRef
                .afterOpened()
                .subscribe(() => notificationRef._dismissAfter(config.duration));
        }
        if (config.announcementMessage) {
            this._live.announce(config.announcementMessage, config.politeness);
        }
    }
    /**
     * Creates a new overlay and places it in the correct location.
     * @param config The user-specified notification toast config.
     */
    _createOverlay(config) {
        const overlayConfig = new OverlayConfig();
        const positionStrategy = this._overlay.position().global();
        // Set horizontal position.
        positionStrategy.centerHorizontally();
        // Set horizontal position.
        if (config.verticalPosition === 'top') {
            positionStrategy.top('0');
        }
        else {
            positionStrategy.bottom('0');
        }
        overlayConfig.positionStrategy = positionStrategy;
        return this._overlay.create(overlayConfig);
    }
    /**
     * Creates an injector to be used inside of a notification toast component.
     * @param config Config that was used to create the notification toast.
     * @param notificationRef toastRef Reference to the notification toast.
     */
    _createInjector(config, notificationRef) {
        const userInjector = config?.viewContainerRef?.injector;
        return Injector.create({
            parent: userInjector || this._injector,
            providers: [
                { provide: SbbNotificationToastRef, useValue: notificationRef },
                { provide: SBB_NOTIFICATION_TOAST_DATA, useValue: config.data },
            ],
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToast, deps: [{ token: i1.Overlay }, { token: i0.Injector }, { token: i2.LiveAnnouncer }, { token: i3.BreakpointObserver }, { token: SbbNotificationToast, optional: true, skipSelf: true }, { token: SBB_NOTIFICATION_TOAST_DEFAULT_OPTIONS }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToast, providedIn: SbbNotificationToastModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.7", ngImport: i0, type: SbbNotificationToast, decorators: [{
            type: Injectable,
            args: [{ providedIn: SbbNotificationToastModule }]
        }], ctorParameters: () => [{ type: i1.Overlay }, { type: i0.Injector }, { type: i2.LiveAnnouncer }, { type: i3.BreakpointObserver }, { type: SbbNotificationToast, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }, { type: SbbNotificationToastConfig, decorators: [{
                    type: Inject,
                    args: [SBB_NOTIFICATION_TOAST_DEFAULT_OPTIONS]
                }] }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SBB_NOTIFICATION_TOAST_ANIMATIONS, SBB_NOTIFICATION_TOAST_DATA, SBB_NOTIFICATION_TOAST_DEFAULT_OPTIONS, SBB_NOTIFICATION_TOAST_DEFAULT_OPTIONS_FACTORY, SbbNotificationToast, SbbNotificationToastConfig, SbbNotificationToastContainer, SbbNotificationToastContainerBase, SbbNotificationToastModule, SbbNotificationToastRef, SbbSimpleNotification };
